<script setup lang="ts">
import { useLocale } from 'vuetify';

import '@/assets/scss/docs.scss';

/**
 * Minimal shell for in-app Markdown docs: top bar with branding and locale switch.
 * Page content (nav, article, TOC) lives under pages/help/.
 */
const route = useRoute();
const { setLocale } = useI18n();
const { current: vuetifyLocale } = useLocale();
const { docLocale, helpPathForLocale } = useDocsRoute();

async function changeDocLocale(locale: 'en' | 'ja') {
  if (locale === docLocale.value) {
    return;
  }
  setLocale(locale);
  vuetifyLocale.value = locale;
  await navigateTo({ path: helpPathForLocale(locale), hash: route.hash || undefined });
}
</script>

<template>
  <VApp
    theme="light"
    class="docs-app"
  >
    <header class="docs-app__bar">
      <div class="docs-app__bar-inner">
        <nuxt-link
          to="/"
          class="docs-app__brand"
        >
          <img
            src="/images/logo.png"
            alt="Suge POS"
            width="28"
            height="28"
            class="docs-app__logo"
          />
          <span class="docs-app__title">Suge POS Docs</span>
        </nuxt-link>

        <div
          class="docs-app__locale-switch"
          role="group"
          aria-label="Documentation language"
        >
          <button
            type="button"
            class="docs-app__locale-btn"
            :class="{ 'docs-app__locale-btn--active': docLocale === 'en' }"
            @click="changeDocLocale('en')"
          >
            English
          </button>
          <button
            type="button"
            class="docs-app__locale-btn"
            :class="{ 'docs-app__locale-btn--active': docLocale === 'ja' }"
            @click="changeDocLocale('ja')"
          >
            日本語
          </button>
        </div>
      </div>
    </header>

    <VMain class="docs-app__main pa-0">
      <div class="docs-app__shell">
        <slot />
      </div>
    </VMain>
  </VApp>
</template>
